import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import ArrowLeft from '@gravity-ui/icons/ArrowLeft';
import ArrowRight from '@gravity-ui/icons/ArrowRight';
import { Button, Icon } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { installableAppUploadImage } from 'client/api/appStoreApi';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { ScreenshotUploader } from 'client/blocks-redesign/FilesUploaders/FilesUploaders';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormErrors } from 'client/hocs/types';
import { AliceApp, Channel } from 'client/model/skill';
import { InstallableAppFormSettings, InstallableAppScreenshot } from 'client/types';
import { translateError } from 'client/utils/errorMessage';
import * as keyset from 'i18n/AppForm';

import { Field } from '../Field/Field';

import styles from './ScreenshotsBlock.module.scss';

const MAX_SCREENSHOTS_COUNT = 10;

const t = i18n(keyset);

interface IScreenshotsBlock {
    fieldTitle: string;
    fieldDescription: string;
    items: InstallableAppScreenshot[];
    updateScreenshots: (screenshots: InstallableAppScreenshot[]) => void;
    disabled: boolean;
    error?: string;
    setErrors: (errors: FormErrors<InstallableAppFormSettings & object>, cb?: () => void) => void;
}

const DisplayScreenShotInDeviceBorder: FC<{
    selectedImageUrl: string;
    channel: AliceApp['channel'];
    loadedScreenshots: number;
}> = ({ selectedImageUrl, channel, loadedScreenshots }) => {
    if (channel === Channel.AutoApp) {
        return (
            <div className={styles.autoAppPreviewContainer}>
                <div
                    className={styles.smartScreenPreviewImage}
                    style={{ backgroundImage: `url(${selectedImageUrl})` }}
                ></div>
                <img
                    className={styles.smartScreenPreviewMockBorder}
                    src={
                        'https://avatars.mds.yandex.net/get-dialogs/399212/screenshot-device-with-screen-empty-border/catalogue-banner-x2'
                    }
                    alt="empty-tv-border"
                />
            </div>
        );
    }

    if (channel === Channel.TvApp) {
        return (
            <div
                className={clx(
                    styles.tvPreviewContainer,
                    loadedScreenshots >= 4 && styles.tvPreviewContainer_bottomMargin,
                )}
            >
                <div className={styles.tvPreviewShadowGenerator}></div>
                <div className={styles.tvPreviewImage} style={{ backgroundImage: `url(${selectedImageUrl})` }}></div>
                <img
                    className={styles.tvPreviewMockBorder}
                    src={
                        'https://avatars.mds.yandex.net/get-dialogs/1644703/screenshot-tv-empty-border/catalogue-banner-x2'
                    }
                    alt="empty-tv-border"
                />
            </div>
        );
    }
    if (channel === Channel.SmartScreenApp) {
        return (
            <div className={styles.smartScreenPreviewContainer}>
                <div
                    className={styles.smartScreenPreviewImage}
                    style={{ backgroundImage: `url(${selectedImageUrl})` }}
                ></div>
                <img
                    className={styles.smartScreenPreviewMockBorder}
                    src={
                        'https://avatars.mds.yandex.net/get-dialogs/399212/screenshot-device-with-screen-empty-border/catalogue-banner-x2'
                    }
                    alt="empty-tv-border"
                />
            </div>
        );
    }
    return null;
};

export const ScreenshotsBlock: FC<IScreenshotsBlock> = ({
    fieldTitle,
    fieldDescription,
    items,
    updateScreenshots,
    disabled,
    error,
    setErrors,
}) => {
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [screenshotsModificationIsAllowed, setScreenshotModificationAllowed] = useState(true);
    // const [uploadedError, setUploadedError] = useState('');
    const { aliceApp } = useContext(CurrentAliceAppContext)!;

    useEffect(() => {
        if (items.length) {
            setSelectedImageUrl(items[0].imageUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (!screenshotsModificationIsAllowed) return;
            setScreenshotModificationAllowed(false);
            const screnshotsCopy = [...items];

            for (const file of acceptedFiles) {
                // setUploadedError('');
                try {
                    const uploadRes = await installableAppUploadImage(
                        aliceApp.id,
                        aliceApp.channel,
                        file,
                        'appScreenshot',
                    );
                    screnshotsCopy.push({
                        imageUrl: uploadRes.url,
                        imageId: uploadRes.id,
                    });
                    setSelectedImageUrl(uploadRes.url);
                    updateScreenshots(screnshotsCopy);
                } catch (error) {
                    if (error && typeof error === 'object' && 'message' in error) {
                        setErrors({ screenshots: translateError(error.message as string) });
                    } else {
                        setErrors({ screenshots: 'Error in screenshots loading' });
                    }
                }
            }

            setScreenshotModificationAllowed(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [items, updateScreenshots, aliceApp.id, screenshotsModificationIsAllowed, setScreenshotModificationAllowed],
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        accept: ['image/png', 'image/jpeg'],
    });

    const selectScreenshot = (selectedScreenshotUrl: string) => {
        setSelectedImageUrl(selectedScreenshotUrl);
    };

    const deleteSelectedScreenshot = () => {
        const updatedScreenshots = items.filter((screenshot) => screenshot.imageUrl !== selectedImageUrl);
        if (updatedScreenshots.length) {
            setSelectedImageUrl(updatedScreenshots[0].imageUrl);
        }
        updateScreenshots(updatedScreenshots);
    };

    const deleteAllScreenshots = () => {
        updateScreenshots([]);
    };

    return (
        <ErrorBoundary>
            <div className={styles.screenshotsBlockContainer}>
                <Field fieldTitle={fieldTitle} fieldDescription={fieldDescription} error={error}>
                    {!!items?.length && (
                        <header className={styles.screenshotsHeader}>
                            <span className={styles.screenshotsHeader__countBlock}>
                                {t('screenshots-count', { count: items?.length, maxCount: MAX_SCREENSHOTS_COUNT })}
                            </span>
                            <div className={styles.screenshotsHeader__buttonsBlock}>
                                <Button
                                    onClick={deleteSelectedScreenshot}
                                    size="m"
                                    view="flat-info"
                                    disabled={disabled || items.length === 0}
                                >
                                    {t('delete')}
                                </Button>
                                <Button
                                    onClick={deleteAllScreenshots}
                                    size="m"
                                    view="flat-info"
                                    disabled={disabled || items.length === 0}
                                >
                                    {t('delete-all')}
                                </Button>
                            </div>
                        </header>
                    )}
                    <div className={styles.screenshotsPreviewGallery}>
                        {items.map((screenshotData, i) => (
                            <div
                                key={screenshotData.imageUrl}
                                className={clx(
                                    styles.screenshotPreviewCard,
                                    (aliceApp.channel === Channel.SmartScreenApp ||
                                        aliceApp.channel === Channel.AutoApp) &&
                                        styles.screenshotPreviewCard_smartScreen,
                                    screenshotData.imageUrl === selectedImageUrl && styles.screenshotPreviewCard_active,
                                )}
                                style={{ backgroundImage: `url(${screenshotData.imageUrl})` }}
                                onClick={() => selectScreenshot(screenshotData.imageUrl)}
                            >
                                {selectedImageUrl === screenshotData.imageUrl &&
                                    screenshotsModificationIsAllowed &&
                                    !disabled && (
                                        <div className={styles.movePreviewButtonsBlock}>
                                            <Button
                                                className={styles.movePreviewButton}
                                                size="s"
                                                onClick={(_e) => {
                                                    if (i > 0) {
                                                        const itemsCopy = [...items];
                                                        itemsCopy.splice(i, 1);
                                                        itemsCopy.splice(i - 1, 0, screenshotData);
                                                        updateScreenshots(itemsCopy);
                                                    }
                                                }}
                                            >
                                                <Icon data={ArrowLeft} />
                                            </Button>
                                            <Button
                                                className={styles.movePreviewButton}
                                                size="s"
                                                onClick={(_e) => {
                                                    if (i < items.length - 1) {
                                                        const itemsCopy = [...items];
                                                        itemsCopy.splice(i, 1);
                                                        itemsCopy.splice(i + 1, 0, screenshotData);
                                                        updateScreenshots(itemsCopy);
                                                    }
                                                }}
                                            >
                                                <Icon data={ArrowRight} />
                                            </Button>
                                        </div>
                                    )}
                            </div>
                        ))}
                        {!disabled && items.length <= 10 && (
                            <div>
                                <div className={styles.uploaderContainer} {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <ScreenshotUploader
                                        channel={aliceApp.channel}
                                        isDragActive={isDragActive}
                                        isLoading={!screenshotsModificationIsAllowed}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </Field>
                {!!items.length && selectedImageUrl && (
                    <DisplayScreenShotInDeviceBorder
                        channel={aliceApp.channel}
                        selectedImageUrl={selectedImageUrl}
                        loadedScreenshots={items?.length}
                    />
                )}
            </div>
        </ErrorBoundary>
    );
};
